import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { filter, map, switchMap, take } from 'rxjs';
import { canAccess, getLandingRoute } from '../../shared/constants/role-access';
import { UserRole } from '../../shared/models/user.model';
import { AuthService } from '../../shared/services/auth.service';

/**
 * Guard factory restricting a route to the given roles.
 * ADMIN is always allowed. Unauthorized users are redirected to their landing route.
 */
export const roleGuard = (...roles: UserRole[]): CanActivateFn => {
  return () => {
    const authService = inject(AuthService);
    const router = inject(Router);

    // isAuthenticated$ is null until AuthService has resolved the token (validating it or
    // fetching /user/currentUser on a hard reload). Wait for that resolution before reading
    // currentUser$ — otherwise a still-null currentUser$ reads as "no role" and misroutes an
    // actually-authenticated user to their landing route.
    return authService.isAuthenticated$.pipe(
      filter(isAuthenticated => isAuthenticated !== null),
      take(1),
      switchMap(() => authService.currentUser$.pipe(take(1))),
      map(user => {
        if (canAccess(user?.role, roles)) {
          return true;
        }
        router.navigate([getLandingRoute(user?.role)]);
        return false;
      })
    );
  };
};
